import catalogJson from "./image-models.json";

export type ImageModelGroup = "recommended" | "fast" | "fidelity";

/**
 * Product use-cases for the canvas debit modal / agent filter.
 * - t2i: generate stills / keyframes from prompt
 * - edit: change content while keeping the photo's shape
 * - outpaint: fill gray borders (expand) — not for landscape↔portrait flips
 * - recompose: redraw the scene into a new aspect (edit API, no gray canvas)
 */
export type ImageModelUseCase = "t2i" | "edit" | "outpaint" | "recompose";

export type ImageModelOption = {
  id: string;
  label: string;
  provider: "poyo" | "gemini" | "kling";
  poyoModel?: string;
  geminiModel?: string;
  costSettingId: string;
  creditsPerImage: number;
  cogsUsdPerImage: number;
  minCreditsPerImage: number;
  default?: boolean;
  recommended?: boolean;
  premium?: boolean;
  group?: ImageModelGroup;
  aliases?: string[];
  /** When missing, model is treated as unscoped (shown for every use-case). Prefer explicit tags. */
  useCases?: ImageModelUseCase[];
};

export const IMAGE_MODEL_GROUP_ORDER: ImageModelGroup[] = [
  "recommended",
  "fast",
  "fidelity",
];

export const IMAGE_MODEL_GROUP_LABELS: Record<ImageModelGroup, string> = {
  recommended: "Recomendados",
  fast: "Rápido",
  fidelity: "Alta fidelidade",
};

export const DEFAULT_IMAGE_MODEL_BY_USE_CASE: Record<ImageModelUseCase, string> = {
  t2i: "poyo-wan27-image",
  edit: "poyo-seedream-lite",
  outpaint: "poyo-wan27-image",
  recompose: "poyo-seedream-lite",
};

export const IMAGE_MODEL_CATALOG: ImageModelOption[] = catalogJson as ImageModelOption[];

export function getImageModelById(id: string): ImageModelOption | undefined {
  return IMAGE_MODEL_CATALOG.find(
    (m) =>
      m.id === id ||
      m.poyoModel === id ||
      m.geminiModel === id ||
      (m.aliases || []).includes(id),
  );
}

export function defaultImageModel(): ImageModelOption {
  return IMAGE_MODEL_CATALOG.find((m) => m.default) || IMAGE_MODEL_CATALOG[0];
}

export function pickImageModel(id?: string): ImageModelOption {
  const found = id ? getImageModelById(id) : undefined;
  if (found) return found;
  return defaultImageModel();
}

/** Worker / SQS `model` field (PoYo slug or Gemini API id). Never shown in UI. */
export function imageJobModel(model: ImageModelOption): string {
  if (model.provider === "poyo") return model.poyoModel || model.id;
  if (model.provider === "gemini") return model.geminiModel || model.id;
  return model.id;
}

/** Cheapest sibling for studio stage preview (2K → 1K when the catalog has both). */
export function lowestPreviewImageModelId(id?: string): string {
  const model = pickImageModel(id);
  if (model.id === "poyo-qwen21-2k") return "poyo-qwen21";
  if (model.id === "poyo-qwen3-pro") return "poyo-qwen3";
  if (model.id === "poyo-wan27-image-pro") return "poyo-wan27-image";
  if (model.id === "poyo-seedream-pro") return "poyo-seedream-lite";
  if (model.id === "poyo-flux3-klein-2k" || model.id === "poyo-flux3-klein-4k") return "poyo-flux3-klein";
  if (model.id === "poyo-nano-banana-21-2k" || model.id === "poyo-nano-banana-21-4k") {
    return "poyo-nano-banana-21";
  }
  if (model.id === "poyo-ideogram45-high") return "poyo-ideogram45";
  return model.id;
}

export function modelSupportsUseCase(model: ImageModelOption, useCase: ImageModelUseCase): boolean {
  if (!model.useCases?.length) return true;
  return model.useCases.includes(useCase);
}

export function listImageModelsForUseCase(useCase: ImageModelUseCase): ImageModelOption[] {
  return listImageModelsGrouped().filter((m) => modelSupportsUseCase(m, useCase));
}

export function defaultImageModelForUseCase(useCase: ImageModelUseCase): ImageModelOption {
  const preferred = getImageModelById(DEFAULT_IMAGE_MODEL_BY_USE_CASE[useCase]);
  if (preferred && modelSupportsUseCase(preferred, useCase)) return preferred;
  return listImageModelsForUseCase(useCase)[0] || defaultImageModel();
}

/** Keep the current id when it fits the use-case; otherwise fall back to the use-case default. */
export function pickImageModelForUseCase(useCase: ImageModelUseCase, id?: string): ImageModelOption {
  const found = id ? getImageModelById(id) : undefined;
  if (found && modelSupportsUseCase(found, useCase)) return found;
  return defaultImageModelForUseCase(useCase);
}

export type AspectOrientation = "landscape" | "portrait" | "square";

export function aspectOrientation(ratio: number): AspectOrientation {
  if (!Number.isFinite(ratio) || ratio <= 0) return "square";
  if (ratio > 1.05) return "landscape";
  if (ratio < 0.95) return "portrait";
  return "square";
}

/**
 * True when the frame’s long axis changes (landscape↔portrait, square→9:16, etc.).
 * Outpaint / “expand borders” is the wrong tool: the photo would need new content on the
 * long side (or a crop). The modal should hide expand and offer recompose/edit or crop.
 */
export function isOrientationFlip(fromRatio: number, toRatio: number): boolean {
  return aspectOrientation(fromRatio) !== aspectOrientation(toRatio);
}

export function listImageModelsGrouped(): ImageModelOption[] {
  const rank = (group?: ImageModelGroup) => {
    const i = group ? IMAGE_MODEL_GROUP_ORDER.indexOf(group) : 99;
    return i < 0 ? 99 : i;
  };
  return [...IMAGE_MODEL_CATALOG].sort((a, b) => {
    const g = rank(a.group) - rank(b.group);
    if (g !== 0) return g;
    if (a.recommended && !b.recommended) return -1;
    if (!a.recommended && b.recommended) return 1;
    if (a.default && !b.default) return -1;
    if (!a.default && b.default) return 1;
    return a.creditsPerImage - b.creditsPerImage;
  });
}
